import type { ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, SectionTitle } from './Card';
import { formatAmd, formatAxis, formatPercent } from '@/utils/format';
import { v, font } from '@/theme/tokens';

/** One point per month. Values are numbers or null — null gaps stay gaps. */
export interface MonthPoint {
  month: string;
  [series: string]: string | number | null;
}

export function ChartCard({ title, right, children, height = 260 }: {
  title: string;
  right?: ReactNode;
  children: ReactNode;
  height?: number;
}) {
  return (
    <Card>
      <SectionTitle right={right}>{title}</SectionTitle>
      <div style={{ width: '100%', height }} aria-label={title} role="img">
        {children}
      </div>
    </Card>
  );
}

const axisProps = {
  tick: { fontSize: 11, fill: 'var(--ink-muted)', fontFamily: font.mono },
  axisLine: { stroke: 'var(--line)' },
  tickLine: false as const,
};

function MoneyTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: { name: string; value: number | null; color: string }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rt-tooltip" style={{ padding: '10px 12px', fontSize: 12.5 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.name} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '1px 0' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: entry.color }} />
          <span style={{ color: v('ink-sub'), flex: 1 }}>{entry.name}</span>
          <span style={{ fontFamily: font.mono, fontWeight: 600 }}>
            {entry.value == null ? '—' : formatAmd(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

function PercentTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: { name: string; value: number | null; color: string }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rt-tooltip" style={{ padding: '10px 12px', fontSize: 12.5 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.name} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '1px 0' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: entry.color }} />
          <span style={{ color: v('ink-sub'), flex: 1 }}>{entry.name}</span>
          <span style={{ fontFamily: font.mono, fontWeight: 600 }}>
            {entry.value == null ? '—' : formatPercent(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

const legendStyle = { fontSize: 12, color: 'var(--ink-sub)' };

export function RevenueTrendChart({ data }: { data: MonthPoint[] }) {
  return (
    <ResponsiveContainer>
      <AreaChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid stroke="var(--line-soft)" vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={(value: number) => formatAxis(value)} width={52} />
        <Tooltip content={<MoneyTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        <Area
          type="monotone" dataKey="Выручка" stroke="var(--chart-blue)" strokeWidth={2.2}
          fill="var(--chart-blue)" fillOpacity={0.08} connectNulls={false} dot={{ r: 2.5 }}
        />
        <Line
          type="monotone" dataKey="Среднее 3 мес." stroke="var(--ink-faint)" strokeWidth={1.6}
          strokeDasharray="5 5" dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function ProfitTrendChart({ data }: { data: MonthPoint[] }) {
  return (
    <ResponsiveContainer>
      <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid stroke="var(--line-soft)" vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={(value: number) => formatAxis(value)} width={52} />
        <Tooltip content={<MoneyTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        <Line type="monotone" dataKey="Валовая" stroke="var(--chart-teal)" strokeWidth={2} dot={{ r: 2.5 }} />
        <Line type="monotone" dataKey="Операционная" stroke="var(--chart-blue)" strokeWidth={2} dot={{ r: 2.5 }} />
        <Line type="monotone" dataKey="Чистая" stroke="var(--chart-violet)" strokeWidth={2} dot={{ r: 2.5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function MarginTrendChart({ data }: { data: MonthPoint[] }) {
  return (
    <ResponsiveContainer>
      <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid stroke="var(--line-soft)" vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis
          {...axisProps}
          tickFormatter={(value: number) => formatPercent(value, 0)}
          width={44}
        />
        <Tooltip content={<PercentTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        <Line type="monotone" dataKey="Валовая маржа" stroke="var(--chart-teal)" strokeWidth={2} dot={{ r: 2.5 }} />
        <Line type="monotone" dataKey="Операционная маржа" stroke="var(--chart-blue)" strokeWidth={2} dot={{ r: 2.5 }} />
        <Line type="monotone" dataKey="Чистая маржа" stroke="var(--chart-violet)" strokeWidth={2} dot={{ r: 2.5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function ExpenseStackChart({ data, series }: {
  data: MonthPoint[];
  series: { key: string; color: string }[];
}) {
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid stroke="var(--line-soft)" vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={(value: number) => formatAxis(value)} width={52} />
        <Tooltip content={<MoneyTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        {series.map((entry) => (
          <Bar key={entry.key} dataKey={entry.key} stackId="expenses" fill={entry.color} maxBarSize={36} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function SingleSeriesChart({ data, name, color, percent }: {
  data: MonthPoint[];
  name: string;
  color: string;
  percent?: boolean;
}) {
  return (
    <ResponsiveContainer>
      <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid stroke="var(--line-soft)" vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis
          {...axisProps}
          tickFormatter={(value: number) => (percent ? formatPercent(value, 0) : formatAxis(value))}
          width={percent ? 44 : 52}
        />
        <Tooltip content={percent ? <PercentTooltip /> : <MoneyTooltip />} />
        <Line type="monotone" dataKey={name} stroke={color} strokeWidth={2.2} dot={{ r: 2.5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export interface BridgeStep {
  label: string;
  /** Positive adds to the running total, negative subtracts. A step with
   * `isTotal` restarts the bridge from this value instead of stepping from
   * the previous bar — that is how Revenue and Net profit anchor the chart. */
  value: number | null;
  isTotal?: boolean;
}

interface BridgeDatum {
  name: string;
  base: number;
  size: number;
  display: number;
  tone: 'total' | 'up' | 'down';
}

function BridgeTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: { payload: BridgeDatum }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const datum = payload[0].payload;
  return (
    <div className="rt-tooltip" style={{ padding: '10px 12px', fontSize: 12.5 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: font.mono, fontWeight: 600 }}>{formatAmd(datum.display)}</div>
    </div>
  );
}

const BRIDGE_COLOR: Record<BridgeDatum['tone'], string> = {
  total: 'var(--chart-slate)',
  up: 'var(--positive)',
  down: 'var(--negative)',
};

/** Revenue → Gross profit → Operating profit → Net profit as a waterfall.
 *
 * Every number plotted comes straight from the backend's `current` KPIs —
 * this only works out where each bar starts and ends, it never derives a new
 * financial figure.
 */
export function ProfitBridgeChart({ steps }: { steps: BridgeStep[] }) {
  let running = 0;
  const data: BridgeDatum[] = [];
  for (const step of steps) {
    if (step.value == null) continue;
    if (step.isTotal) {
      running = step.value;
      data.push({ name: step.label, base: 0, size: step.value, display: step.value, tone: 'total' });
      continue;
    }
    const start = running;
    running += step.value;
    data.push({
      name: step.label,
      base: Math.min(start, running),
      size: Math.abs(step.value),
      display: step.value,
      tone: step.value >= 0 ? 'up' : 'down',
    });
  }

  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid stroke="var(--line-soft)" vertical={false} />
        <XAxis dataKey="name" {...axisProps} interval={0} tick={{ ...axisProps.tick, fontSize: 10.5 }} />
        <YAxis {...axisProps} tickFormatter={(value: number) => formatAxis(value)} width={52} />
        <Tooltip content={<BridgeTooltip />} />
        <Bar dataKey="base" stackId="bridge" fill="transparent" isAnimationActive={false} />
        <Bar dataKey="size" stackId="bridge" radius={[3, 3, 3, 3]} maxBarSize={44}>
          {data.map((entry) => (
            <Cell key={entry.name} fill={BRIDGE_COLOR[entry.tone]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
